import { useCallback, useEffect, useState } from 'react';
import MinusIcon from '../assets/minus-icon.svg?react';
import PlusIcon from '../assets/plus-alternate-icon.svg?react';
import DeleteIcon from '../assets/delete-icon.svg?react';
import { Heading3 } from './shared/Heading';
import { Button } from './shared/Button/Button';
import { GuestsLegend } from './GuestsLegend';
import { useRootStore } from '../stores/useRootStore';
import { observer } from 'mobx-react-lite';
import { HotelChildCategory } from '../apiMethods/getConfig';
import { nanoid } from 'nanoid';
import { TooltipStatic } from './Tooltip/TooltipStatic';
import { isEqual } from 'lodash';
import type { GuestRoom } from '../stores/paramsStore';

type GuestCategoryProps = {
  name: string;
  count: number;
  onChange: (count: number) => void;
  max?: number;
  min?: number;
  showTooltip?: boolean;
  annotation?: string;
};

const OVERBOOKING_WARNING =
  'Делюкс - максимум 3 гостя + ребенок до 3 лет. Семейный люкс - максимум 4 гостя + ребенок до 3 лет';

const GuestCategory = ({
  name,
  count: _count,
  onChange,
  max,
  min,
  annotation,
}: GuestCategoryProps) => {
  const [count, setCount] = useState(_count);

  const increment = () => {
    if (max && count >= max) {
      return;
    }

    const newCount = count + 1;
    setCount(newCount);
    onChange(newCount);
  };

  const decrement = () => {
    if (min && count <= min) {
      return;
    }

    const newCount = count <= 0 ? 0 : count - 1;
    setCount(newCount);
    onChange(newCount);
  };

  return (
    <div className="mb-2 flex items-center justify-between text-neutral-900">
      <div className="flex flex-col">
        <span className="text=[28px] font-normal">{name}</span>
        <span className="text-[10px] font-light leading-3">{annotation}</span>
      </div>

      <span className="flex items-center">
        <Button style="secondary" variant="link" onClick={decrement}>
          <MinusIcon />
        </Button>
        <span className="w-8 text-center text-[16px] font-normal">{count}</span>
        <Button style="secondary" variant="link" onClick={increment}>
          <PlusIcon />
        </Button>
      </span>
    </div>
  );
};

const RoomHeader = ({
  onDelete,
  roomIndex,
  showTooltip,
}: {
  onDelete?: () => void;
  roomIndex: number;
  showTooltip: boolean;
}) => (
  <div className="mb-3 flex justify-between">
    <TooltipStatic isOpen={showTooltip} anchor={<h6 className="font-medium">Номер {roomIndex + 1}</h6>}>
      <div>{OVERBOOKING_WARNING}</div>
    </TooltipStatic>

    {onDelete && (
      <div
        className="flex cursor-pointer text-sm text-neutral-70 hover:text-neutral-700"
        onClick={onDelete}
      >
        <span>Удалить номер</span>
        <DeleteIcon className="h-5 w-5" />
      </div>
    )}
  </div>
);

type GuestRoomProps = {
  id: string;
  params: {
    adults: number;
    childs: ChildCategoryState[];
  };
};

const GuestRoom = observer(
  ({
    room,
    onDelete,
    roomIndex,
    maxAdults,
    childs,
    onChange,
  }: {
    room: GuestRoomProps;
    onDelete?: (id: string) => void;
    roomIndex: number;
    maxAdults: number;
    childs: HotelChildCategory[];
    onChange: (params: Partial<GuestRoomProps['params']>) => void;
  }) => {
    const onChildrenChange = (childCategoryId: number, count: number) => {
      const newChilds = mapToCountable(childs).map(cc => {
        if (cc.id == childCategoryId) {
          cc.count = count;
          return cc;
        }

        const existingCount = room.params.childs.find(x => x.id === cc.id)?.count;
        cc.count = existingCount ?? 0;
        return cc;
      });

      onChange({
        childs: newChilds,
      });
    };

    const { paramsStore } = useRootStore();

    const { adults: adultsCount } = room.params;

    const childsCount = room.params.childs
      .filter(c => c.use_in_calc)
      .reduce((count, c) => count + c.count, 0);

    const isCorpMode = paramsStore.rateMode == 'CORP';
    const isOverbooked = adultsCount + childsCount > 3;

    return (
      <article className="my-4 border-b border-neutral-30 pb-5">
        <RoomHeader
          onDelete={onDelete ? () => onDelete?.(room.id) : undefined}
          roomIndex={roomIndex}
          showTooltip={isCorpMode && isOverbooked}
        />

        <GuestCategory
          name="Взрослые"
          count={room.params.adults}
          max={maxAdults}
          min={1}
          onChange={count => onChange({ adults: count })}
        />
        {childs.map(c => (
          <GuestCategory
            key={c.id}
            name={c.name}
            count={room.params.childs.find(child => child.id == c.id)?.count || 0}
            onChange={count => onChildrenChange(c.id, count)}
            max={c.max_childs}
            annotation={`От ${c.from_age} до ${c.to_age} лет`}
          />
        ))}
      </article>
    );
  },
);

type ChildCategoryState = { id: number; code: string; count: number; use_in_calc: boolean };

const mapToCountable = (childCategories: HotelChildCategory[]) => {
  return childCategories.map(x => ({
    id: x.id,
    code: x.code,
    count: 0,
    use_in_calc: x.use_in_culc,
  }));
};

const getNewRoom = (childs: ChildCategoryState[]) => {
  return {
    id: nanoid(),
    params: {
      adults: 2,
      childs,
    },
  };
};

type GuestRoomsProps = {
  guestRooms: GuestRoom[];
  onChange: (guestRooms: GuestRoom[]) => void;
  childCategories: HotelChildCategory[];
  maxRooms?: number;
  maxAdults?: number;
};

export const GuestRooms = ({
  guestRooms: _guestRooms,
  onChange,
  childCategories,
  maxRooms = 999,
  maxAdults = 999,
}: GuestRoomsProps) => {
  const [guestRooms, setGuestRooms] = useState<GuestRoomProps[]>(_guestRooms);

  const onGuestRoomsChange = (rooms: GuestRoomProps[]) => {
    setGuestRooms(rooms);
    onChange(rooms);
  };

  useEffect(() => {
    const storeParams = _guestRooms.map(room => room.params);
    const currentParams = guestRooms.map(room => room.params);

    if (!isEqual(storeParams, currentParams)) {
      onGuestRoomsChange(_guestRooms);
    }
  }, [_guestRooms]);

  /** initialize rooms with child categories */
  useEffect(() => {
    if (guestRooms.length || !childCategories) {
      return;
    }
    const newRoom = getNewRoom(mapToCountable(childCategories));
    onGuestRoomsChange([newRoom]);
  }, [guestRooms, childCategories]);

  const addGuestRoom = () => {
    if (guestRooms.length >= maxRooms) {
      return;
    }

    const newRoom = getNewRoom(mapToCountable(childCategories) || []);
    const newRooms = guestRooms.concat(newRoom);
    onGuestRoomsChange(newRooms);
  };

  const onDelete = (roomId: string) => {
    if (guestRooms.length == 1) {
      return;
    }
    onGuestRoomsChange(guestRooms.filter(room => room.id != roomId));
  };

  const updateRoomParams = useCallback(
    (roomId: string, roomParams: Partial<GuestRoomProps['params']>) => {
      const updatedRooms = guestRooms.map(room => {
        if (room.id === roomId) {
          return { ...room, params: { ...room.params, ...roomParams } };
        }
        return room;
      });

      onGuestRoomsChange(updatedRooms);
    },
    [guestRooms, setGuestRooms],
  );

  return (
    <>
      <div className="border border-neutral-30 px-5 pb-4 pt-8 xl:flex-1 xl:p-8 xl:pb-6">
        <Heading3 className="text-center">Гости</Heading3>
        {guestRooms.map((room, i) => (
          <GuestRoom
            key={room.id}
            roomIndex={i}
            room={room}
            onDelete={guestRooms.length > 1 ? onDelete : undefined}
            maxAdults={maxAdults}
            childs={childCategories || []}
            onChange={params => updateRoomParams(room.id, params)}
          />
        ))}

        {maxRooms > guestRooms.length && (
          <div className="text-center">
            <Button style="secondary" onClick={addGuestRoom} className="w-full xl:w-auto">
              Добавить номер
            </Button>
          </div>
        )}
      </div>
      <GuestsLegend />
    </>
  );
};

GuestRooms.displayName = 'Guests';
